import { EnumOrganizationMemberPermissions } from '@/helpers/enums/permission.enum';

/**
 * Watar simplified role model (internal-team-mode orgs only).
 *
 * Roles on this platform are org-scoped permission sets in the
 * OrganizationMember context (created via `org-permissions-management`).
 * Internal-team orgs run a reduced 4-role model — self-paced learning with
 * supervisor oversight, no instructors or coaches:
 *
 *   - Watar Admin       -> full org admin. Mirrors the Admin template
 *                          (Predefined Organization Member clone): the
 *                          FullAccess bypass PLUS the two literal grants the
 *                          admin-perms backfill added (CreateCourses +
 *                          ModifySkillsDevelopment), because the dashboard
 *                          gates its create-affordances on the literal
 *                          presence of those slugs, not on the bypass.
 *   - Watar New Joiner  -> learner tier ("Default Member" parity: courses +
 *   - Watar Intern         communities + events). The three learner roles
 *   - Watar Employee       carry identical grants on purpose — they exist as
 *                          distinct roles because the onboarding journey's
 *                          roleFilter targets steps per role.
 *
 * IMPORTANT: the onboarding-step `roleFilter` matches against these exact
 * role names/slugs downstream. Do not rename them casually.
 *
 * Note on "Watar Admin": every org also owns a hidden "<OrgName> Admin"
 * set (slug `organization_member_admin_<orgId>`) cloned at org creation
 * and attached to the Organization context — it never appears in the
 * OrganizationMember role list/dropdowns and is only assigned through the
 * admin-invitations flow. The visible "Watar Admin" role created here
 * carries the same grant profile and deliberately shares the name, so the
 * onboarding roleFilter matches admins from either path.
 *
 * Everything here is flag-gated on `internal-team-mode`; non-flagged orgs
 * see zero behavior change.
 */

export interface WatarRoleDefinition {
  /** Permission-set name — the identity the onboarding roleFilter matches. */
  name: string;
  /** Org-member permission slugs the role must carry (additive floor). */
  permissions: EnumOrganizationMemberPermissions[];
}

const LEARNER_TIER_PERMISSIONS: EnumOrganizationMemberPermissions[] = [
  EnumOrganizationMemberPermissions.AccessCourses,
  EnumOrganizationMemberPermissions.AccessCommunities,
  EnumOrganizationMemberPermissions.AccessEvents,
];

export const WATAR_ROLE_DEFINITIONS: WatarRoleDefinition[] = [
  {
    name: 'Watar Admin',
    permissions: [
      EnumOrganizationMemberPermissions.FullAccess,
      EnumOrganizationMemberPermissions.CreateCourses,
      EnumOrganizationMemberPermissions.ModifySkillsDevelopment,
    ],
  },
  { name: 'Watar New Joiner', permissions: LEARNER_TIER_PERMISSIONS },
  { name: 'Watar Intern', permissions: LEARNER_TIER_PERMISSIONS },
  { name: 'Watar Employee', permissions: LEARNER_TIER_PERMISSIONS },
];

/**
 * The org-creation flow clones an "Instructor" and a "Coach" role for every
 * org with these exact slugs (see himam-nest
 * organizations-repository.service.ts). They are the instructor/coach role
 * artifacts an internal-team org must not surface.
 */
const HIDDEN_ROLE_SLUG_PATTERN = /^organization_member_(instructor|coach)_\d+$/;

/**
 * Filters instructor/coach role sets out of an org's role list when the org
 * runs internal-team-mode. Pass-through (same array) when the flag is off.
 */
export const filterInternalTeamRoleSets = <T extends { slug: string }>(
  sets: T[],
  internalTeamMode: boolean
): T[] => {
  if (!internalTeamMode) return sets;
  return sets.filter((set) => !HIDDEN_ROLE_SLUG_PATTERN.test(set.slug));
};

/**
 * Parent permission-category slugs that only exist to configure instructor /
 * coach capabilities. Hidden from the role permission matrix in
 * internal-team-mode (children like `instructor_courses` / `coach_abilities`
 * hang under these parents and disappear with them).
 */
export const INTERNAL_TEAM_HIDDEN_CATEGORY_SLUGS: readonly string[] = [
  'instructor_permissions',
  'coach_permissions',
];

/**
 * Computes the ADD-only grant payload that brings one role up to its
 * definition. Additive on purpose (mirrors the admin-perms backfill: never
 * removes), so re-running is always safe and admin customizations survive.
 * Slugs that cannot be resolved to a system-permission id are skipped —
 * a partially seeded permission table must not block the rest.
 */
export const diffRoleGrants = (
  desiredSlugs: EnumOrganizationMemberPermissions[],
  grantedSlugs: ReadonlySet<string> | string[],
  permissionIdBySlug: ReadonlyMap<string, number>
): { entityId: number; addOrRemove: 'ADD' }[] => {
  const granted =
    grantedSlugs instanceof Set ? grantedSlugs : new Set(grantedSlugs);
  const additions: { entityId: number; addOrRemove: 'ADD' }[] = [];
  for (const slug of desiredSlugs) {
    if (granted.has(slug)) continue;
    const entityId = permissionIdBySlug.get(slug);
    if (entityId === undefined) continue;
    additions.push({ entityId, addOrRemove: 'ADD' });
  }
  return additions;
};
